import * as ReactRedux from 'react-redux';
import React from 'react';
import PropTypes from 'prop-types';
import CustomPropTypes from 'CustomPropTypes';
import PopupGroupEditingBox from 'PopupGroup/PopupGroupEditingBox';
import PopupDoneBox from 'Popups/SelfServeBuilderPopups/PopupDoneBox';
import classNames from 'classnames';
import { bindActionCreators } from 'redux';
import { Tooltip } from 'react-bootstrap';
import { beginEditingGroup } from 'PopupGroup/PopupGroupActions';
import { beginEditingPopup } from 'actions';
import { SelfServeBuilderPopupTypes } from 'Popups';
import styles from '../../sass/main.scss';

const { MULTIPLE_CHOICE } = SelfServeBuilderPopupTypes;

const POPUP_BOX_PADDING = 6;
const DONE_POPUP_HEIGHT = 40;

class PopupGroupBox extends React.Component {
  constructor(props) {
    super(props);

    this.beginEditingGroup = this.beginEditingGroup.bind(this);
    this.getHeight = this.getHeight.bind(this);
    this.getBorderColor = this.getBorderColor.bind(this);
    this.getStyle = this.getStyle.bind(this);
    this.showError = this.showError.bind(this);
    this.onResize = this.onResize.bind(this);

    this.popupGroupBoxRef = React.createRef();

    this.state = {
      showError: false,
      inputHeight: 0,
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const popupGroup = this.props.popupGroup;
    const groupedPopupIds = popupGroup.fieldIds;
    const initialPopupId = groupedPopupIds[0];

    if (!initialPopupId) {
      return;
    }
    const inputHeight = this.getHeight();
    if (this.props.showPopupCreationError !== prevProps.showPopupCreationError) {
      this.setState({ showError: this.props.showPopupCreationError });
      this.props.addPaddingTop(initialPopupId, inputHeight);
    }

    if (prevState.inputHeight !== inputHeight) {
      this.setState({ inputHeight });
      this.props.addPaddingTop(initialPopupId, inputHeight);
    }
  }

  onResize(inputHeight) {
    this.setState({ inputHeight });
  }

  getBorderColor() {
    const { showError } = this.state;
    const {
      boxPopupNameErrorColor,
      sectionOrganizerDividerTextHoverColor,
    } = styles;
    return showError ? boxPopupNameErrorColor : sectionOrganizerDividerTextHoverColor;
  }

  getHeight() {
    const node = this.popupGroupBoxRef.current;
    if (node) {
      return node.clientHeight;
    }
    return 0;
  }

  getStyle() {
    const { popupGroup, isEditingGroup, popupsInGroup } = this.props;
    const groupedPopupIds = popupGroup.fieldIds;
    const initialPopup = popupsInGroup[groupedPopupIds[0]];
    const yTranslateOffset = isEditingGroup ?
      POPUP_BOX_PADDING + this.state.inputHeight :
      DONE_POPUP_HEIGHT;
    const yTranslate = initialPopup.y - yTranslateOffset;
    const borderColor = this.getBorderColor();
    const style = {
      transform: `translate(${initialPopup.x}px, ${yTranslate}px)`,
      border: `1px solid ${borderColor}`,
      boxShadow: `0 0 5px 0 ${borderColor}`,
    };
    return style;
  }

  showError(showError) {
    if (this.state.showError === showError) {
      return;
    }
    this.setState({ showError });
  }

  beginEditingGroup() {
    this.props.beginEditingGroup();
    this.props.beginEditingPopup();
  }

  render() {
    const { groupId, isEditingGroup, popupGroup } = this.props;
    let { showError } = this.state;
    if (!isEditingGroup) {
      showError = false;
    }
    const groupedPopupIds = popupGroup.fieldIds;

    if (groupedPopupIds.length === 0) {
      return null;
    }

    const borderColor = this.getBorderColor();
    const style = this.getStyle();
    const groupPopupClasses = classNames(
      'group-box-popup',
      {
        'show-done-box': !isEditingGroup,
      },
    );

    let instructionText = 'add more checkboxes';
    let pluralType = 'checkboxes';

    if (popupGroup.type === MULTIPLE_CHOICE) {
      instructionText = 'add more choices';
      pluralType = 'choices';
    }

    return (
      <div
        className={groupPopupClasses}
        data-for={`${groupId}-tooltip`}
        data-tip
        id={groupId}
        ref={this.popupGroupBoxRef}
        style={style}
      >
        {showError && isEditingGroup &&
          <Tooltip
            className="in box-popup-name-input-tooltip group-box-popup-tooltip"
            id={`${groupId}-tooltip`}
            placement="right"
          >
            <div className="tooltip-content">
              Type a question, name all choices and click done. Or delete the {pluralType}.
            </div>
          </Tooltip>
        }
        {isEditingGroup &&
          <PopupGroupEditingBox
            addPaddingTop={this.props.addPaddingTop}
            borderColor={borderColor}
            getHeight={this.getHeight}
            groupId={this.props.groupId}
            instructionText={instructionText}
            onResize={this.onResize}
            showError={this.showError}
            popupsInGroup={this.props.popupsInGroup}
            popupGroup={this.props.popupGroup}
          />
        }
        {!isEditingGroup &&
          <PopupDoneBox
            onClick={this.beginEditingGroup}
            label={popupGroup.label}
            superScript={this.props.getSuperScriptValue(groupId, popupGroup.label, true)}
          />
        }
      </div>
    );
  }
}

PopupGroupBox.defaultProps = {
  activePopup: '',
  isEditingGroup: false,
  showPopupCreationError: false,
};

PopupGroupBox.propTypes = {
  addPaddingTop: PropTypes.func.isRequired,
  beginEditingGroup: PropTypes.func.isRequired,
  beginEditingPopup: PropTypes.func.isRequired,
  getSuperScriptValue: PropTypes.func.isRequired,
  groupId: PropTypes.string.isRequired,
  isEditingGroup: PropTypes.bool,
  showPopupCreationError: PropTypes.bool,
  popupGroup: PropTypes.shape({
    label: PropTypes.string,
    type: PropTypes.string,
    pageIndex: PropTypes.number,
    fieldIds: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
  popupsInGroup: CustomPropTypes.hashmapOf(
    PropTypes.shape({
      label: PropTypes.string,
      type: PropTypes.string,
    }),
  ).isRequired,
};

const mapStateToProps = ({ SelfServeBuilderReducer, PopupGroupReducer }) => ({
  isEditingGroup: PopupGroupReducer.isEditingGroup,
  showPopupCreationError: SelfServeBuilderReducer.showPopupCreationError,
});

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    beginEditingGroup,
    beginEditingPopup,
  }, dispatch)
);


export default ReactRedux.connect(mapStateToProps, mapDispatchToProps)(PopupGroupBox);



// WEBPACK FOOTER //
// ./src-react/PopupGroup/PopupGroupBox.jsx